import { Dispatch, SetStateAction } from "react";

// nest.js components
import Link from "next/link";

// @material-tailwind/react components
import { Typography, Chip } from "@material-tailwind/react";

// prop-types
interface Props {
  routes: {
    name: string;
    icon: string;
    pages: {
      name: string;
      label?: string;
    }[];
  }[];
  type: "html" | "react" | "vue" | "angular" | "svelte";
  slug: string;
  mobileNav: boolean;
  setMobileNav: Dispatch<SetStateAction<boolean>>;
}

function Sidenav({ routes, type, slug, mobileNav, setMobileNav }: Props) {
  const colors = {
    html: "from-pink-400 to-pink-600",
    react: "from-blue-400 to-blue-600",
  };

  return (
    <aside
      className={`fixed z-[999] h-screen w-80 pb-4 pr-4 transition-all duration-300 lg:relative lg:left-0 lg:z-10 lg:w-64 lg:pt-20 ${
        mobileNav ? "left-0" : "-left-96"
      }`}
    >
      <div
        className={`fixed top-0 left-0 h-screen w-screen bg-gray-900/20 backdrop-blur-sm transition-opacity duration-300 lg:hidden ${
          mobileNav ? "pointer-events-auto opacity-100" : "pointer-events-none opacity-0"
        }`}
      />
      <div className="fixed h-screen w-80 overflow-y-scroll bg-white pb-48 pt-6 pl-6 lg:w-64 lg:bg-transparent lg:pt-0 lg:pl-0">
        <div className="flex items-center justify-between">
          <Typography variant="h4" color="blue-gray">
            Documentation
          </Typography>
          <Typography
            variant="h5"
            className="mr-3 mt-px cursor-pointer p-2 text-blue-gray-500 transition-colors hover:text-blue-gray-900 lg:hidden"
            onClick={() => setMobileNav(false)}
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              className="h-5 w-5"
              viewBox="0 0 20 20"
              fill="currentColor"
            >
              <path
                fillRule="evenodd"
                d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z"
                clipRule="evenodd"
              />
            </svg>
          </Typography>
        </div>
        <div className="mt-4">
          {routes.map(({ name, icon, pages }, key): any => (
            <div key={key}>
              <div className="mt-8 flex items-center">
                <div
                  className={`${colors[type]} mr-3 grid h-7 w-7 place-items-center rounded-lg bg-gradient-to-tr text-xs text-white`}
                >
                  <i className={icon} />
                </div>
                <Typography color="blue-gray" className="font-bold capitalize">
                  {name}
                </Typography>
              </div>
              <ul className="space-y-1 py-2 pr-2 pl-9">
                {pages.map(({ name: pageName, label }, key) => (
                  <li key={key}>
                    <Link href={`/docs/${type}/${pageName}`} className="flex items-center gap-2">
                      <Typography
                        color="gray"
                        className={`before:content-[' '] relative list-item w-max py-1 px-1 capitalize transition-colors before:absolute before:-left-[25px] before:top-2/4 before:h-1.5 before:w-1.5 before:-translate-y-2/4 before:rounded-full before:transition-colors hover:text-blue-gray-900 hover:before:bg-blue-gray-900 ${
                          pageName === slug
                            ? "font-medium text-blue-gray-900 before:bg-blue-gray-900"
                            : "font-normal text-blue-gray-400 before:bg-blue-gray-300"
                        }`}
                        onClick={() => setMobileNav(false)}
                      >
                        {pageName.includes("-")
                          ? pageName
                              .split("-")
                              .map((el) => (el === "css" ? "CSS" : el === "cli" ? "CLI" : el))
                              .join(" ")
                          : pageName}
                      </Typography>
                      {label && (
                        <Chip
                          size="sm"
                          value={label}
                          variant="ghost"
                          color={
                            label === "new" ? "green" : label === "new example" ? "amber" : "blue"
                          }
                          className="rounded-full capitalize"
                        />
                      )}
                    </Link>
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>
      </div>
    </aside>
  );
}

export default Sidenav;
